import { useNavigate, useParams } from '@tanstack/react-router';
import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { useLot } from '../api/hooks';
import { OfflineBanner, TopBar } from '../components/chrome';
import {
  IconAccessible,
  IconArrowDownToLine,
  IconArrowsVertical,
  IconBolt,
  IconCamera,
  IconClock,
  IconDot,
  IconElevator,
  IconGrid,
  IconHome,
  IconSun,
  IconUmbrella,
  IconUser,
} from '../components/icons';
import { Cta, ErrorState, Loading, Muted, Screen } from '../components/ui';
import { durationLabel, mergeRateTiers, type RateBand, usd } from '../lib/format';

type IconComponent = ComponentType<SVGProps<SVGSVGElement>>;

/** One linear icon per facility code (review §5) — same mapping the glyphs had. */
const AMENITY_ICON: Record<string, IconComponent> = {
  open_24h: IconClock,
  staffed: IconUser,
  underground: IconArrowDownToLine,
  indoor: IconHome,
  outdoor: IconSun,
  no_height_limit: IconArrowsVertical,
  elevator: IconElevator,
  cctv: IconCamera,
  ev_charging: IconBolt,
  accessible: IconAccessible,
  covered: IconUmbrella,
};

export function LotPage() {
  const { t, i18n } = useTranslation();
  const { lotId } = useParams({ from: '/lot/$lotId' });
  const navigate = useNavigate();
  const lot = useLot(lotId);

  if (lot.isPending) {
    return (
      <>
        <TopBar />
        <Screen>
          <Loading />
        </Screen>
      </>
    );
  }

  if (lot.isError || !lot.data) {
    return (
      <>
        <TopBar />
        <Screen>
          <ErrorState message={t('lot.loadFailed')} onRetry={() => void lot.refetch()} />
        </Screen>
      </>
    );
  }

  const detail = lot.data;

  // Fall back to the other language rather than showing nothing: a driver who
  // cannot find the entrance would rather read the wrong language than a blank.
  const accessNote = i18n.resolvedLanguage?.startsWith('zh')
    ? detail.accessNoteZh || detail.accessNoteEn
    : detail.accessNoteEn || detail.accessNoteZh;

  // Structured facts and coded facilities share one icon-row presentation.
  // Everything is translated client-side, so no row can arrive in the wrong
  // language or in a second visual style. The EV amenity flag is dropped when
  // a charger count exists — one fact, one row.
  const features = [
    detail.heightLimitCm
      ? {
          key: 'height_limit',
          icon: IconArrowsVertical,
          label: t('lot.heightLimit', { metres: (detail.heightLimitCm / 100).toFixed(2) }),
        }
      : null,
    detail.evChargers > 0
      ? { key: 'ev_chargers', icon: IconBolt, label: t('lot.evChargers', { count: detail.evChargers }) }
      : null,
    { key: 'capacity', icon: IconGrid, label: t('lot.capacity', { count: detail.capacity }) },
    ...detail.amenities
      .filter((code) => !(code === 'ev_charging' && detail.evChargers > 0))
      .map((code) => ({ key: code, icon: AMENITY_ICON[code] ?? IconDot, label: t(`amenities.${code}`) })),
  ].filter(Boolean) as Array<{ key: string; icon: IconComponent; label: string }>;

  // Bounds are built from raw minutes with durationLabel — the API's per-tier
  // `label` is a server display string and must never render.
  const bandLabel = (band: RateBand) =>
    band.fromMinutes === 0
      ? t('lot.tierUpTo', { duration: durationLabel(band.toMinutes, t) })
      : t('lot.tierOverUpTo', {
          from: durationLabel(band.fromMinutes, t),
          to: durationLabel(band.toMinutes, t),
        });

  return (
    <>
      <TopBar title={detail.name} wide />
      <OfflineBanner />

      {/* The entrance photo is how a driver confirms they are at the right gate
          before paying — a wrong lot means a wasted payment (FR-U-15). This
          full-bleed banner is the phone treatment only: at lg it hides and the
          photo re-renders as a card inside the left grid column instead of
          smearing across an 800px page (review §4.13). */}
      {detail.photos[0] ? (
        <div className="flex h-40 items-end bg-surface-strong p-3 lg:hidden">
          <img
            src={detail.photos[0]}
            alt={t('lot.entrancePhoto')}
            className="absolute inset-x-0 h-40 w-full object-cover"
          />
          <span className="relative rounded-md bg-ink/75 px-2.5 py-1.5 text-[11px] text-white">
            {t('lot.entrancePhoto')}
          </span>
        </div>
      ) : null}

      {/* Lot detail is one of the three wide desktop pages (review §4.13). */}
      <Screen wide>
        {/* Desktop two-column (review §4.13): photo/info/address/amenities
            left, rate card + CTA right. It is a grid, never floats. Below lg
            the wrapper and both columns are plain gap-4 flex columns — the
            same stacking Screen itself does — so the phone layout is
            pixel-identical to the pre-grid markup. */}
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start lg:gap-x-8">
          <div className="flex flex-col gap-4">
            {/* Desktop-only photo card: same photo, contained in the column
                (the mobile banner above is lg:hidden). rounded-lg keeps the
                8px card radius system (review §5). */}
            {detail.photos[0] ? (
              <div className="relative hidden h-52 overflow-hidden rounded-lg bg-surface-strong lg:block">
                <img
                  src={detail.photos[0]}
                  alt={t('lot.entrancePhoto')}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span className="absolute bottom-3 left-3 rounded-md bg-ink/75 px-2.5 py-1.5 text-[11px] text-white">
                  {t('lot.entrancePhoto')}
                </span>
              </div>
            ) : null}

            {/* The top bar already carries the lot's official (English) name —
                the body only adds what the bar lacks. Lot-name convention
                (review §4.12): the official name is the name; the Chinese
                alias never fuses into it and instead leads the muted secondary
                line, exactly as the home list rows render it. */}
            <Muted>{[detail.nameZh, detail.address].filter(Boolean).join(' · ')}</Muted>

            {/* A missing photo is a data gap, not a load failure, so it must
                not occupy a photo-sized block. Code and directions do the
                photo's confirm-the-location job instead — and on desktop this
                compact strip stays in the left column rather than spanning the
                page like a fault (review §4.13). */}
            {!detail.photos[0] ? (
              <div className="flex items-center justify-between gap-3 rounded-lg border border-dashed border-hairline-strong px-3.5 py-2.5">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[13px] font-medium">{t('lot.noPhoto')}</span>
                  <span className="text-xs text-muted">{t('lot.lotCode', { code: detail.shortCode })}</span>
                </div>
                <a
                  href={`https://www.google.com/maps/dir/?api=1&destination=${detail.latitude},${detail.longitude}`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex min-h-11 shrink-0 items-center text-[13px] font-semibold text-accent"
                >
                  {t('lot.directions')}
                </a>
              </div>
            ) : null}

            {accessNote ? (
              <div className="flex flex-col gap-1 rounded-lg bg-surface-muted p-3">
                <span className="text-xs font-semibold">{t('lot.accessTitle')}</span>
                <p className="text-[13px] leading-relaxed text-muted">{accessNote}</p>
              </div>
            ) : null}

            {detail.activeEvent ? (
              <div className="flex flex-col gap-1 rounded-lg border-[1.5px] border-ink p-3">
                <span className="text-[15px] font-semibold">{detail.activeEvent.name}</span>
                <Muted>{t('lot.eventActive', { price: usd(detail.activeEvent.priceCents) })}</Muted>
              </div>
            ) : null}

            {features.length > 0 ? (
              <div className="grid grid-cols-2 gap-2">
                {features.map((feature) => (
                  <div
                    key={feature.key}
                    className="flex min-h-[52px] items-center gap-2 rounded-lg border border-hairline px-3"
                  >
                    <feature.icon className="h-[18px] w-[18px] shrink-0 text-accent" />
                    <span className="flex-1 text-[13px] font-medium">{feature.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>

          {/* Right column: what the driver acts on. Sticky below the 52px top
              bar so the price and the single primary CTA stay in view while
              the left column scrolls; position only kicks in at lg. */}
          <div className="flex flex-col gap-4 lg:sticky lg:top-[68px]">
            <div className="overflow-hidden rounded-lg border border-hairline">
              <div className="flex justify-between bg-surface-muted px-4 py-3 text-xs text-muted">
                <span>{t('lot.rateCard')}</span>
                <span>{t('lot.totalPrice')}</span>
              </div>
              {mergeRateTiers(detail.rateCard).map((band) => (
                <div
                  key={band.toMinutes}
                  className="flex items-center justify-between border-t border-hairline px-4 py-3"
                >
                  <span className="text-[15px]">{bandLabel(band)}</span>
                  <span className="num text-[15px] font-semibold">{usd(band.totalCents)}</span>
                </div>
              ))}
            </div>

            <Cta
              label={t('lot.choose')}
              note={t('lot.lotCode', { code: detail.shortCode })}
              onClick={() => void navigate({ to: '/lot/$lotId/plates', params: { lotId } })}
            />
          </div>
        </div>
      </Screen>
    </>
  );
}
